import { CommunicationEnvelopeSummary } from '@litlingo/client';
import { ATTACHMENT_ICON, COMMENT_ICON, RECIPIENTS_ICON, TAGS_ICON } from 'constants/envelopeIcons';
import moment from 'moment';
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getCustomerDomain, getSurroundingContextRulesIds } from 'selectors/auth';
import getSelectedEnvelope, { getSelectedSection } from 'selectors/envelopeReview';
import {
  getRecipientsFromCommunication,
  getThreadAttachments,
  getThreadComments,
  getThreadHasHitsFromSelected,
  getThreadRecipients,
} from 'selectors/envelopes';
import { useSelector } from 'store';
import { isFileCommunication } from 'utils/communications';
import { reverse } from 'utils/urls';
import ReviewValueLabel from '../ReviewValueLabel';

type ComponentProps = {
  envelopeSummary: CommunicationEnvelopeSummary;
  isEntryPoint: boolean;
  border?: boolean;
  selectedThreads: string[];
  handleSelectThread: (e: React.ChangeEvent<HTMLInputElement>, envelopeId: string) => void;
};

const ThreadItem: React.FC<ComponentProps> = ({
  envelopeSummary,
  isEntryPoint,
  border = true,
  selectedThreads,
  handleSelectThread,
}) => {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const location = useLocation();
  const history = useHistory();
  const customerDomain = useSelector(getCustomerDomain);
  const hasHitsFromSelected = useSelector((state) =>
    getThreadHasHitsFromSelected(state, envelopeSummary.envelope.uuid)
  );
  const attachments = useSelector((state) =>
    getThreadAttachments(state, envelopeSummary.envelope.uuid)
  );
  const users = useSelector((state) => getThreadRecipients(state, envelopeSummary.envelope.uuid));
  const comments = useSelector((state) => getThreadComments(state, envelopeSummary.envelope.uuid));

  const selectedEnvelope = useSelector(getSelectedEnvelope);
  const recipients = useSelector(getRecipientsFromCommunication);

  const selectedSection = useSelector(getSelectedSection);
  const contextRules = useSelector(getSurroundingContextRulesIds);

  const date = moment(envelopeSummary.sent_at);
  const recipientsDiff = users.length - recipients.length;

  const getHasEventHits = (): boolean =>
    envelopeSummary.events.length > 0 &&
    envelopeSummary.events.some((e) => e.rule_uuid && !contextRules.includes(e.rule_uuid));

  const handleThreadClick = (): void => {
    const path = reverse({
      routeName: 'envelope-detail',
      routeParams: { envelopeId: envelopeSummary.envelope.uuid },
      queryParams: location.search,
      customerDomain,
    });

    history.push(path, { from_thread: true });
  };

  return (
    <div
      className={`flex flex-row items-center w-full hover:bg-litlingo-gray-2 ${
        selectedSection === 'thread' &&
        hasHitsFromSelected &&
        selectedEnvelope?.uuid !== envelopeSummary.envelope.uuid
          ? 'bg-litlingo-focus-area-background'
          : 'bg-white'
      } ${border ? 'border-b border-litlingo-gray-1' : ''}`}
    >
      <div className="flex flex-row items-center gap-2 w-full pl-3">
        <input
          id="select-checkbox"
          type="checkbox"
          className={`form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out ${
            envelopeId === envelopeSummary.envelope.uuid ? '' : 'mr-3'
          }`}
          checked={selectedThreads.includes(envelopeSummary.envelope.uuid)}
          onChange={(e): void => handleSelectThread(e, envelopeSummary.envelope.uuid)}
        />

        {envelopeId === envelopeSummary.envelope.uuid && (
          <div>
            <svg
              width="4"
              height="52"
              viewBox="0 0 4 52"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="4" height="52" fill="#F8A01A" />
            </svg>
          </div>
        )}

        <button
          type="button"
          onClick={handleThreadClick}
          className={`relative flex focus:outline-none w-full `}
        >
          <div className="relative w-full flex flex-row justify-between pr-4 pt-2 pb-1 text-small">
            <div className="flex flex-col gap-1 w-3/5">
              <div className="flex flex-row items-center gap-1">
                <span className="truncate break-words">
                  {isFileCommunication(envelopeSummary.envelope.communications?.[0] || null)
                    ? envelopeSummary.envelope.communications?.[0]?.subject
                    : envelopeSummary.envelope.created_by?.name}
                </span>
                {isEntryPoint && (
                  <span className="self-start">
                    <svg
                      width="8"
                      height="8"
                      viewBox="0 0 8 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="8" height="8" rx="4" fill="#858585" />
                    </svg>
                  </span>
                )}
              </div>
              <div className="flex flex-row gap-x-2 text-litlingo-gray-4">
                {recipientsDiff !== 0 && (
                  <div className="flex flex-row gap-1 items-center">
                    {RECIPIENTS_ICON}{' '}
                    <span>
                      {recipientsDiff > 0
                        ? `+${Math.abs(recipientsDiff)}`
                        : `-${Math.abs(recipientsDiff)}`}
                    </span>
                  </div>
                )}
                {attachments.length > 0 && (
                  <div className="flex flex-row gap-1 items-center">
                    <span className="w-3">{ATTACHMENT_ICON}</span> <span>{attachments.length}</span>
                  </div>
                )}
                {envelopeSummary?.envelope?.tags && envelopeSummary?.envelope?.tags?.length > 0 && (
                  <div className="flex flex-row gap-1 items-center">
                    {TAGS_ICON} <span>{envelopeSummary.envelope.tags?.length}</span>
                  </div>
                )}
                {comments.length > 0 && (
                  <div className="flex flex-row gap-1 items-center">
                    <span className="w-3">{COMMENT_ICON()}</span> <span>{comments.length}</span>
                  </div>
                )}
                <div className="flex flex-row gap-1 items-center">
                  {envelopeSummary.envelope.review_value &&
                    envelopeSummary.envelope.review_value !== 'pending' && (
                      <ReviewValueLabel
                        reviewValue={envelopeSummary.envelope.review_value}
                        isConfirmed={envelopeSummary.is_review_confirmed}
                      />
                    )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1 items-end">
              <div>{date.format('MMM Do yyyy')}</div>
              <div className="text-litlingo-gray-4">{date.utc().local().format('hh:mm A')}</div>
            </div>
            {getHasEventHits() && (
              <div className="absolute right-0 top-0">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0 0H20V20L10 10L0 0Z" fill="#FFC945" />
                </svg>
              </div>
            )}
          </div>
        </button>
      </div>
    </div>
  );
};

export default ThreadItem;
